/**
 * Simple fuzzy search utilities for exercise filtering
 * Following CLAUDE.md principles: simple, maintainable, no bloat
 */

/**
 * Normalize text for fuzzy matching
 */
export function normalizeText(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[^\w\s]/g, '')
}

/**
 * Simple fuzzy search that checks if search terms are contained in the target
 * More sophisticated than exact match, but simpler than complex fuzzy algorithms
 */
export function fuzzyMatch(searchTerm: string, targetText: string): boolean {
  const normalizedSearch = normalizeText(searchTerm)
  const normalizedTarget = normalizeText(targetText)
  
  // Empty search matches everything
  if (!normalizedSearch) return true
  
  // Split search into words and check if all words are contained in target
  const searchWords = normalizedSearch.split(' ').filter(word => word.length > 0)
  
  return searchWords.every(word => normalizedTarget.includes(word))
}

/**
 * Filter array of items with fuzzy search
 */
export function fuzzyFilter<T>(
  items: T[], 
  searchTerm: string, 
  getText: (item: T) => string
): T[] {
  if (!searchTerm.trim()) return items
  
  return items.filter(item => fuzzyMatch(searchTerm, getText(item)))
}

/**
 * Score items for ranking (simple scoring based on match position)
 */
export function fuzzyScore(searchTerm: string, targetText: string): number {
  const normalizedSearch = normalizeText(searchTerm)
  const normalizedTarget = normalizeText(targetText)
  
  if (!normalizedSearch) return 0
  
  // Higher score for matches at the beginning
  const firstWordMatch = normalizedTarget.startsWith(normalizedSearch)
  const exactMatch = normalizedTarget === normalizedSearch
  const containsMatch = normalizedTarget.includes(normalizedSearch)
  
  if (exactMatch) return 100
  if (firstWordMatch) return 80
  if (containsMatch) return 60
  
  // Check for partial word matches
  const searchWords = normalizedSearch.split(' ')
  const matchedWords = searchWords.filter(word => normalizedTarget.includes(word))
  const wordMatchRatio = matchedWords.length / searchWords.length
  
  return Math.floor(wordMatchRatio * 40)
}

/**
 * Filter and sort items with fuzzy search
 */
export function fuzzyFilterAndSort<T>(
  items: T[],
  searchTerm: string,
  getText: (item: T) => string
): T[] {
  if (!searchTerm.trim()) return items
  
  return items
    .map(item => ({
      item,
      score: fuzzyScore(searchTerm, getText(item))
    }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ item }) => item)
}